import React from "react";
import { Link, NavLink } from "react-router-dom";
import { useLang } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { Menu, X, Fish } from "lucide-react";
import { useState } from "react";

const langs = [{ code: "en", label: "EN" }, { code: "hu", label: "HU" }];

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/lakes", label: t("nav.lakes") },
    { to: "/catches", label: t("nav.catches") },
    { to: "/rules", label: t("nav.rules") },
    { to: "/booking", label: t("nav.booking") },
    { to: "/events", label: t("nav.events") },
  ];

  const linkClass = ({ isActive }) =>
    `text-sm tracking-wide transition-colors ${isActive ? "text-primary font-semibold" : "text-ink-soft hover:text-primary"}`;

  return (
    <header className="sticky top-0 z-50 bg-bg-paper/85 backdrop-blur-xl border-b border-line" data-testid="site-navbar">
      <div className="container-deg flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <Fish className="text-primary group-hover:text-accent transition-colors" strokeWidth={1.5} size={22} />
          <span className="font-serif text-xl tracking-tight text-primary">Dég Dusk</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <div className="flex items-center text-xs">
            {langs.map((l, i) => (
              <React.Fragment key={l.code}>
                <button
                  onClick={() => setLang(l.code)}
                  className={`px-1 transition-colors ${lang === l.code ? "text-primary font-bold" : "text-ink-soft hover:text-primary"}`}
                  data-testid={`lang-switch-${l.code}`}
                >
                  {l.label}
                </button>
                {i === 0 && <span className="text-line">/</span>}
              </React.Fragment>
            ))}
          </div>

          {user ? (
            <div className="flex items-center gap-3">
              {user.role === "admin" && (
                <Link to="/admin" className="text-sm text-accent hover:text-accent-hover" data-testid="nav-admin-link">
                  {t("nav.admin")}
                </Link>
              )}
              <Link to="/account" className="text-sm text-ink-soft hover:text-primary" data-testid="nav-account-link">
                {user.name}
              </Link>
              <button onClick={logout} className="text-sm text-ink-soft hover:text-primary" data-testid="nav-logout-button">
                {t("nav.logout")}
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link to="/login" className="text-sm text-ink-soft hover:text-primary" data-testid="nav-login-link">{t("nav.login")}</Link>
              <Link to="/register" className="btn-primary text-xs px-4 py-2" data-testid="nav-register-link">{t("nav.register")}</Link>
            </div>
          )}
        </div>

        <button className="md:hidden text-primary" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-line bg-bg-paper" data-testid="mobile-menu">
          <div className="container-deg py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} className={linkClass} onClick={() => setOpen(false)}>
                {l.label}
              </NavLink>
            ))}
            <div className="flex gap-4 pt-2">
              {langs.map((l) => (
                <button key={l.code} onClick={() => setLang(l.code)}
                  className={`text-xs px-2 ${lang === l.code ? "text-primary font-bold" : "text-ink-soft"}`}>
                  {l.label}
                </button>
              ))}
            </div>
            {user ? (
              <>
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="text-sm text-accent">{t("nav.admin")}</Link>}
                <button onClick={() => { logout(); setOpen(false); }} className="text-left text-sm">{t("nav.logout")}</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="text-sm">{t("nav.login")}</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="text-sm text-accent">{t("nav.register")}</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
